import { Carousel } from '@mantine/carousel';
import { Badge, ColorSwatch, Divider, Image, NumberFormatter, Card, Collapse, Tooltip, Group, ActionIcon } from '@mantine/core';
import { IHotelOnlineBookingList } from 'libs/data-access/types';
import { GoDotFill } from "react-icons/go";
import { calculateDateForFreeCancellation, groupByKey, isDateAfter } from 'utils/utility';
import moment from 'moment';
import React from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FiCopy } from 'react-icons/fi';

export interface IAvailabilityFacilityResponse {
    facilityGroup: string;
    feeApplied: boolean;
    mandatory: boolean;
    name: string;
};

const HotelRoomDetails = ({ hotel, showCarousel }: {
    hotel: IHotelOnlineBookingList['bookingRooms'];
    showCarousel: boolean;
}) => {
    const [openImages, setOpenImages] = React.useState<number | null>(null);

    const handleGroupingOfRoomFacilities = (data: any[]) => {
        const groupedFacilities = groupByKey(data, "facilityGroup");
        return groupedFacilities;
    };

    const handleCopyRoomName = (roomName: string) => {
        navigator.clipboard.writeText(roomName);
    };

    return (
        <div className="space-y-8">
            {hotel?.map((room, index) => {
                const groupedRoomFacilities = handleGroupingOfRoomFacilities(room.hotelBookingFacilities);
                return (
                    <Card
                        key={index}
                        shadow="md"
                        radius="lg"
                        withBorder
                        className="transition-transform hover:scale-[1.01] hover:shadow-xl bg-white/90"
                        p="lg"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                {room.roomName} - {room.boardNameResponses?.[0]?.boardName}
                                <Tooltip label="Copy room name" withArrow>
                                    <ActionIcon variant="light" size="sm" onClick={() => handleCopyRoomName(room.roomName)}>
                                        <FiCopy size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </p>
                            {/* Optionally, add a 'View Details' button here */}
                        </div>
                        {/* Carousel */}
                        {showCarousel && room?.roomImageUrl?.length > 0 && (
                            <div className="mb-6">
                                <Carousel
                                    withIndicators
                                    loop
                                    height={300}
                                    className="rounded overflow-hidden w-full md:w-3/5 mx-auto"
                                    slidesToScroll={2}
                                >
                                    {room?.roomImageUrl?.map((image, imgIdx) => (
                                        <Carousel.Slide key={imgIdx}>
                                            <Image
                                                src={image}
                                                alt="Room image"
                                                fit="cover"
                                                radius="md"
                                                className="cursor-pointer hover:opacity-90 transition"
                                                onClick={() => setOpenImages(index)}
                                            />
                                        </Carousel.Slide>
                                    ))}
                                </Carousel>
                                {/* Modal for image zoom can be added here */}
                            </div>
                        )}
                        {/* Facilities Section */}
                        <div className="mb-4">
                            <div className="mb-2 flex items-center gap-2">
                                <span className="text-lg font-semibold">Room Facilities</span>
                                <Tooltip label="See all amenities and charges" withArrow>
                                    <BsInfoCircle size={18} className="text-blue-500" />
                                </Tooltip>
                            </div>
                            <div>
                                {room?.hotelBookingFacilities?.length > 0 ? (
                                    Object.keys(groupedRoomFacilities).map((groupName, groupIdx) => (
                                        <div key={groupIdx + "_facility_group"} className="flex flex-col gap-2 mb-3">
                                            <span className="font-semibold text-gray-700">{groupName}</span>
                                            <div className="flex flex-wrap gap-2">
                                                {groupedRoomFacilities[groupName].map((facility: any, i: number) => (
                                                    <Tooltip
                                                        key={i + "_hotel_facility"}
                                                        label={facility.mandatory ? "Mandatory Amenity" : facility.feeApplied ? "Chargeable" : "Optional"}
                                                        withArrow
                                                    >
                                                        <span
                                                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${facility.mandatory ? "bg-green-50 text-green-700 border-green-200" : facility.feeApplied ? "bg-yellow-50 text-yellow-700 border-yellow-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}
                                                        >
                                                            <GoDotFill size={12} />
                                                            <span className="capitalize">{facility.name}</span>
                                                            {facility.feeApplied && <span className="ml-1">(₹)</span>}
                                                        </span>
                                                    </Tooltip>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="h-25 flex items-center">
                                        <p className="text-slate-400 text-sm">Facility details are not available for this room</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Cancellation Policies Section */}
                        <div className="mb-4">
                            <div className="mb-2 flex items-center">
                                <span className="text-lg font-semibold">Cancellation Policies</span>
                            </div>
                            {room.roomRateKey.cancellationPolicies?.length > 0 ? (
                                room.roomRateKey.cancellationPolicies?.map((policy, i) => (
                                    <div className="flex flex-col gap-3 mb-2" key={i + "_cancellation_policy"}>
                                        {isDateAfter(
                                            moment().format("YYYY-MM-DDTHH:mm:ssZ"),
                                            calculateDateForFreeCancellation(policy?.from)
                                        ) ? (
                                            <Badge color="red" variant="light">Refund is Not Available</Badge>
                                        ) : null}
                                        {i === 0 ? (
                                            <div className='flex items-center justify-between'>
                                                <div className="text-green-600">
                                                    <span>Free until <span className="font-semibold text-sm">
                                                        {calculateDateForFreeCancellation(policy?.from)}
                                                    </span>
                                                    </span>
                                                </div>
                                            </div>
                                        ) : null}
                                        <div className="flex items-center justify-between text-red-600 text-sm">
                                            <span>After <span className="fw-bold">
                                                {calculateDateForFreeCancellation(policy?.from)}
                                            </span>
                                            </span>
                                            <span className="">
                                                <label className="font-semibold">Charges:</label>
                                                {policy?.amount && <span>{" "}{<NumberFormatter
                                                    thousandSeparator
                                                    prefix="₹ "
                                                    value={policy?.amount || 0}
                                                />}</span>}
                                                {policy?.percent && <span>{policy?.percent}%</span>}
                                                {policy?.numberOfNights && <span>{policy?.numberOfNights} Night</span>}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="h-25 flex items-center">
                                    <p className="text-slate-400 text-sm">Cancellation policy is not available to show for this room</p>
                                </div>
                            )}
                        </div>

                        {/* Promotions Section */}
                        <div className="mb-4">
                            <div className="mb-2 flex items-center">
                                <span className="text-lg font-semibold">Promotions</span>
                            </div>
                            {room.roomRateKey.promotions?.length ? (
                                <div className="flex flex-col gap-2">
                                    {room.roomRateKey.promotions?.map((promotion: any, i: number) => (
                                        <div key={i + "_promotion"} className="mb-1">
                                            <p className="font-semibold">{promotion.name || ""}</p>
                                            <p className="text-sm text-slate-400">{promotion.remark || ""}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="h-25 flex items-center">
                                    <p className="text-slate-400 text-sm">Promotion is not available for this room</p>
                                </div>
                            )}
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}

export default HotelRoomDetails;
